import React, { Component } from "react";
import { Row, Col, Spinner } from "react-bootstrap";
import WidgetRenderer from "./WidgetRenderer";
import WidgetLoader from "./common/WidgetLoader";
import configData from "../config.json";

/**
 * WidgetPageRenderer - Fetches page layout from C_Widget_framework/get_page_config,
 * renders Bootstrap Row/Col grid with WidgetRenderer in each cell.
 * Class component per project convention.
 *
 * Props:
 *   - pageRoute: The route key to fetch page config for
 */
export default class WidgetPageRenderer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pageConfig: null,
      loading: true,
      error: null,
      sId: sessionStorage.getItem("sessionId"),
      uId: sessionStorage.getItem("uId"),
      pageFilters: {},
    };
  }

  componentDidMount() {
    this.fetchPageConfig();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.pageRoute !== this.props.pageRoute) {
      this.fetchPageConfig();
    }
  }

  fetchPageConfig = () => {
    this.setState({ loading: true, error: null });

    // Check sessionStorage cache (5-min TTL)
    const cacheKey = `wf_page_${this.props.pageRoute}`;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { data, _ts } = JSON.parse(cached);
        if (Date.now() - _ts < 300000) {
          this.setState({ pageConfig: data, loading: false });
          return;
        }
        sessionStorage.removeItem(cacheKey);
      }
    } catch (e) { /* ignore */ }

    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);
    formData.append("pageRoute", this.props.pageRoute);

    fetch(configData.api_url + "C_Widget_framework/get_page_config", {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === "success" && data.pageConfig) {
          const pc = data.pageConfig;
          // Cache in sessionStorage
          try {
            sessionStorage.setItem(
              cacheKey,
              JSON.stringify({ data: data.pageConfig, _ts: Date.now() })
            );
          } catch (e) {
            // sessionStorage full, ignore
          }
          this.setState({ pageConfig: data.pageConfig, loading: false });
        } else {
          console.warn("[WPR] API returned non-success:", data);
          this.setState({
            error: data.message || "Failed to load page configuration",
            loading: false,
          });
        }
      })
      .catch((err) => {
        this.setState({ error: "Network error loading page", loading: false });
        console.error("[WPR] fetch error:", err);
      });
  };

  handleWidgetEvent = (eventName, sourceWidgetId, eventData) => {
    if (eventName === "filter_applied") {
      this.setState({ pageFilters: eventData || {} });
    } else if (eventName === "filter_cleared") {
      this.setState({ pageFilters: {} });
    }
  };

  renderCell = (cell) => {
    if (!cell.widget_instances_Id || !cell.typeKey) {
      return null;
    }

    return (
      <Col
        key={cell.layout_cells_Id}
        md={parseInt(cell.colSpan) || 12}
        className={cell.cssClass || ""}
      >
        <WidgetRenderer
          widgetConfig={cell}
          pageRoute={this.props.pageRoute}
          onEvent={this.handleWidgetEvent}
          pageFilters={this.state.pageFilters}
          cellConfig={{
            colSpan: cell.colSpan,
            cssClass: cell.cssClass,
          }}
        />
      </Col>
    );
  };

  renderRow = (row) => {
    if (!row.cells || row.cells.length === 0) return null;

    return (
      <Row
        key={row.layout_rows_Id}
        className={row.cssClass || ""}
        style={row.rowHeight && row.rowHeight !== "auto" ? { minHeight: row.rowHeight } : {}}
      >
        {row.cells.map((cell) => this.renderCell(cell))}
      </Row>
    );
  };

  render() {
    const { pageConfig, loading, error } = this.state;

    if (loading) {
      return (
        <div className="page-title">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
            <Spinner animation="border" variant="primary" />
            <span className="ms-2">Loading page...</span>
          </div>
        </div>
      );
    }

    if (error) {
      return <WidgetLoader error={error} onRetry={this.fetchPageConfig} />;
    }

    if (!pageConfig || !pageConfig.rows) {
      return <WidgetLoader error="Page configuration is empty" />;
    }

    return (
      <div className="page-title">
        <div className="row">
          <div className="col-md-12 col-sm-12 col-xs-12">
            <div className="x_panel">
              <div className="x_content">
                {pageConfig.pageTitle && (
                  <div>
                    <h3 className="logoskybluecolor" style={{ textAlign: "start" }}>
                      {pageConfig.pageIcon && <i className={`${pageConfig.pageIcon} me-2`}></i>}
                      {pageConfig.pageTitle}
                    </h3>
                  </div>
                )}
                {pageConfig.rows.map((row) => this.renderRow(row))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
